import { useCallback, useEffect, useRef, useState } from 'react';
import gql from 'graphql-tag';
import { useLazyQuery } from "@apollo/react-hooks";
import { isEmpty } from "lodash";

import { useAppState } from "../../contexts/AppContext";
import { getAuthExpiry, hasTokenExpired } from "../../includes/auth";

const GET_HOME_USER_QUERY: any = gql`
    query getHomeUser {
        getHomeUser {
            id
            FirstName
            Surname
            Email
            IsEmailVerified
            IsSocialConfirmed
            HasViewedGettingStarted
            CountryCodeFromIp
            PostCode
            WhatBestDescribesYou
            LastLoginType
            StripeCustomerID
            EmailVerification {
                id
                IsValid
            }
            Subscription {
                id
                CurrentPeriodEnd
                NiceCurrentPeriodEnd
                IsValid
                Quantity
                PaymentIntent
                Price
                ProductName
                SubscriptionID
                CancelStatus
                CancelScheduleRequestedAt
                PaymentStatus
                FirstPaymentStatus
                BillingHistory {
                    id
                    InvoiceID
                    Currency
                    Amount
                    Card
                    CardType
                    Created
                    ReceiptURL
                }
            }
            BillingAddress {
                Phone
                Line
                City
                State
                CountryCode
                PostCode
            }
        }
    }
`;

export type HomeUser = {
    id: string,
    FirstName: string,
    Surname: string,
    Email: string,
    IsEmailVerified: boolean,
    IsSocialConfirmed: boolean,
    HasViewedGettingStarted: boolean,
    PreviousEmail: string,
    PostCode: string,
    WhatBestDescribesYou: string,
    LastLoginType: string,
    StripeCustomerID: string,
    EmailVerification: EmailVerification,
    Subscription: Subscription,
};

export type EmailVerification = {
    IsValid: boolean,
};

export type Subscription = {
    id: string,
    NiceCurrentPeriodEnd: string,
    IsValid: boolean,
    Quantity: number,
    PaymentIntent: string,
    Price: string,
    ProductName: string,
    SubscriptionID: string,
    PaymentStatus: string,
    CancelStatus: string,
    CancelScheduleRequestedAt: string,
    BillingHistory: [BillingHistory],
};

export type BillingHistory = {
    id: string,
    InvoiceID: string,
    Currency: string,
    Amount: number,
    Card: string,
    CardType: string,
    Created: string,
    ReceiptURL: string,
};

const UserSession: any = () => {
    const [appState, setAppState] = useAppState();
    const [noUser, setNoUser] = useState(false);
    const user = appState?.user;
    const resolveRef = useRef<any>();
    const rejectRef = useRef<any>();

    const onCompleted = (data: any) => {
        if (resolveRef && resolveRef.current) resolveRef.current(data?.getHomeUser);
    }
    const onError = (error: any) => {
        if (rejectRef && rejectRef.current) rejectRef.current(error);
    }

    const [getData, {called, data, loading, refetch}] = useLazyQuery(GET_HOME_USER_QUERY, {
        onCompleted: onCompleted,
        onError: onError
    });

    const getUser = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (resolveRef) resolveRef.current = resolve;
            if (rejectRef) rejectRef.current = reject;
            getData();
        });
    }, [getData]);

    const refetchUser = useCallback(() => {
        return refetch
            ? refetch()
                .then(response => response?.data?.getHomeUser)
                .catch(response => response?.errors)
            : getUser();
    }, [refetch, getUser]);

    /**
     * Clears the user.
     */
    const clearUser = useCallback(() => {
        return new Promise((resolve: any, reject) => {
            setAppState({
                user: null,
                authorized: false,
            })
            resolve();
        });
    }, [setAppState]);

    // if no user, or has not expired cookie/header
    useEffect(() => {
        if (!isEmpty(getAuthExpiry()) && !user && !hasTokenExpired()) {
            setNoUser(false);
            getData();
        } else {
            setNoUser(true);
        }
    }, [clearUser, user, getData]);

    // set user if fetched data changes
    useEffect(() => {
        const userData = data?.getHomeUser;
        setAppState({
            hasUserBeenLoaded: !!data || (called && !loading) || noUser,
            clearUser: clearUser,
            getUser: getUser,
            refetchUser: refetchUser,
            user: userData,
        });
    }, [clearUser, data, getUser, refetch, setAppState, called, loading, noUser, refetchUser]);
    return null;
};

export default UserSession;
